import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import { LayoutDashboard, BookOpen, Award, User, LogOut, Menu, X, GraduationCap, ChevronDown, Settings, BookMarked, Heart, ClipboardCheck, Bell, ClipboardList, Tag, Receipt, CreditCard, Code, Users, Video } from 'lucide-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import certifiedTrainerService from '../../services/certifiedTrainer';
import api from '../../services/api';

export interface DashboardOutletContext {
  /** Re-fetch the trainer unread-notification count so the bell / nav badges stay in sync. */
  refreshUnreadCount: () => void;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [siteName, setSiteName] = useState('IABFM');

  const studentNavItems = useMemo(() => [
    { label: t('studentNav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { label: t('studentNav.myCourses'), href: '/my-courses', icon: BookOpen },
    { label: t('studentNav.myExams'), href: '/my-exams', icon: ClipboardList },
    { label: t('studentNav.wishlist'), href: '/wishlist', icon: Heart },
    { label: t('studentNav.certificates'), href: '/certificates', icon: Award },
    { label: t('studentNav.billing'), href: '/billing', icon: Receipt },
    { label: t('studentNav.profile'), href: '/profile', icon: User },
  ], [t]);

  const trainerNavItems = useMemo(() => [
    { label: t('trainerNav.teachingDashboard'), href: '/my-teaching', icon: LayoutDashboard },
    { label: t('trainerNav.myCreatedCourses'), href: '/my-teaching/courses', icon: BookMarked },
    { label: t('trainerNav.videoStatus'), href: '/my-teaching/video-status', icon: Video },
    { label: t('trainerNav.myMembers'), href: '/my-teaching/members', icon: Users },
    { label: t('trainerNav.myExams'), href: '/my-teaching/exams', icon: ClipboardList },
    { label: t('trainerNav.certificates'), href: '/my-teaching/certificates', icon: Award },
    { label: t('trainerNav.myPromoCodes'), href: '/my-teaching/promo-codes', icon: Tag },
    { label: t('trainerNav.approvalCenter'), href: '/my-teaching/approvals', icon: ClipboardCheck },
    { label: t('trainerNav.messageCenter'), href: '/my-teaching/messages', icon: Bell },
    { label: t('trainerNav.paymentConfig'), href: '/my-teaching/stripe-config', icon: CreditCard },
    { label: t('trainerNav.invoiceSettings'), href: '/my-teaching/invoice-settings', icon: Receipt },
    { label: t('trainerNav.openApi'), href: '/my-teaching/open-api', icon: Code },
  ], [t]);

  const isTeachingSection = location.pathname.startsWith('/my-teaching');
  const isTrainer = user?.isCertifiedTrainer || user?.role === 'admin';
  const hasCustomPayment = user?.customPaymentEnabled === true;
  const hasOpenApi = user?.openApiEnabled === true;

  const fetchUnreadCount = useCallback(async () => {
    if (!isTrainer) return;
    try {
      const count = await certifiedTrainerService.getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // Silently fail for badge polling
    }
  }, [isTrainer]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    api.get('/site-content').then((res) => {
      if (res.data?.branding) {
        if (res.data.branding.logoUrl) setLogoUrl(res.data.branding.logoUrl);
        if (res.data.branding.siteName) setSiteName(res.data.branding.siteName);
        if (res.data.branding.faviconUrl) {
          const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
          if (link) link.href = res.data.branding.faviconUrl;
        }
      }
    }).catch(() => {
      // Silently fall back to defaults
    });
  }, []);

  const navItems = useMemo(() => {
    if (isTeachingSection && isTrainer) {
      return trainerNavItems
        .filter((item) => {
          if (item.href === '/my-teaching/stripe-config') return hasCustomPayment;
          if (item.href === '/my-teaching/open-api') return hasOpenApi;
          return true;
        })
        .map((item) =>
          item.href === '/my-teaching/messages' ? { ...item, badge: unreadCount } : item
        );
    }
    return studentNavItems;
  }, [isTeachingSection, isTrainer, unreadCount, hasCustomPayment, hasOpenApi, trainerNavItems, studentNavItems]);

  const sidebarTitle = isTeachingSection ? t('sidebar.trainerPortal') : t('sidebar.memberPortal');

  const outletContext = useMemo<DashboardOutletContext>(
    () => ({ refreshUnreadCount: fetchUnreadCount }),
    [fetchUnreadCount]
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Top Bar */}
      <header className="glass-refined border-b border-gray-200/50 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center">
            <button
              className="lg:hidden p-2 mr-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/" className="flex items-center space-x-3">
              <img src={logoUrl} alt={siteName} className="logo-dark h-20 -my-6" />
              <span className="text-xl font-bold text-gray-900 font-display">{siteName}</span>
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            {/* Portal Switcher */}
            {(user?.isCertifiedTrainer || user?.role === 'admin') && (
              <div className="hidden sm:flex items-center space-x-2 mr-4">
                <Link
                  to="/dashboard"
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    !isTeachingSection
                      ? 'bg-gold/20 text-gold'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('sidebar.member')}
                </Link>
                <Link
                  to="/my-teaching"
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    isTeachingSection
                      ? 'bg-gold/20 text-gold'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('sidebar.certifiedTrainer')}
                </Link>
              </div>
            )}

            {/* Bell icon for notifications */}
            {isTeachingSection && isTrainer && (
              <Link
                to="/my-teaching/messages"
                className="relative p-2 rounded-lg text-gray-500 hover:text-gold hover:bg-gray-100 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}

            {/* Become Certified Trainer Link */}
            {!user?.isCertifiedTrainer && user?.certifiedTrainerStatus !== 'pending' && user?.role !== 'admin' && (
              <Link
                to="/become-certified-trainer"
                className="hidden sm:flex text-sm text-amber-600 hover:text-amber-700 transition-colors"
              >
                {t('sidebar.becomeTrainer')}
              </Link>
            )}

            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="text-sm text-gold hover:text-amber-600 transition-colors flex items-center"
              >
                <Settings className="w-4 h-4 mr-1" />
                {t('common:nav.admin')}
              </Link>
            )}
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-300 border border-gray-200"
              >
                <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-gold" />
                </div>
                <span className="text-gray-800 hidden sm:block">{user?.name}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 glass-elevated rounded-xl py-2 overflow-hidden">
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-3 text-gray-600 hover:text-gold hover:bg-gray-100 transition-colors"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <User className="w-5 h-5 mr-3" />
                    {t('common:nav.profile')}
                  </Link>

                  {/* Mobile portal switcher */}
                  {(user?.isCertifiedTrainer || user?.role === 'admin') && (
                    <>
                      <hr className="my-2 border-gray-200" />
                      <Link
                        to="/dashboard"
                        className="flex items-center px-4 py-3 text-gray-600 hover:text-gold hover:bg-gray-100 transition-colors sm:hidden"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <BookOpen className="w-5 h-5 mr-3" />
                        {t('sidebar.memberPortal')}
                      </Link>
                      <Link
                        to="/my-teaching"
                        className="flex items-center px-4 py-3 text-gray-600 hover:text-gold hover:bg-gray-100 transition-colors sm:hidden"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <BookMarked className="w-5 h-5 mr-3" />
                        {t('sidebar.trainerPortal')}
                      </Link>
                    </>
                  )}

                  {/* Become a certified trainer link for non-trainers */}
                  {!user?.isCertifiedTrainer && user?.certifiedTrainerStatus !== 'pending' && user?.role !== 'admin' && (
                    <>
                      <hr className="my-2 border-gray-200" />
                      <Link
                        to="/become-certified-trainer"
                        className="flex items-center px-4 py-3 text-amber-600 hover:text-amber-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <GraduationCap className="w-5 h-5 mr-3" />
                        {t('sidebar.becomeTrainer')}
                      </Link>
                    </>
                  )}

                  <hr className="my-2 border-gray-200" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-gray-600 hover:text-red-600 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    {t('common:nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <Sidebar navItems={navItems} title={sidebarTitle} />
        </div>

        {/* Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="w-64 h-full" onClick={(e) => e.stopPropagation()}>
              <Sidebar navItems={navItems} title={sidebarTitle} />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 luxury-bg">
          <Outlet context={outletContext} />
        </main>
      </div>
    </div>
  );
}
